import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Typography, Box, Button, Stack } from '@mui/material';
import {
  ArrowLeft,
  MapPin,
  Wifi,
  Car,
  UtensilsCrossed,
  Dumbbell,
  Waves,
  Heart,
  Share2,
  Coffee,
  TreePine,
  Home as HomeIcon,
  BedDouble,
  Users,
  Check,
  X,
  Clock,
  Ban,
  ShieldCheck,
  Lock,
  ChevronLeft,
  ChevronRight,
  Plane,
  TrainFront,
  Bus,
  Truck,
} from 'lucide-react';
import axios from 'axios';
import { KeyUri } from '../key';
import { BookingModal } from './BookingModal';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { trackEvent } from '../utils/metaPixel';
import SafeImage, { NoImage, pickImage, normalizeImages } from '../Global/SafeTmage';

// ---- Brand palette (used by the untouched Location block) ----
const BRAND = {
  50: '#f0fdf4',
  100: '#dcfce7',
  500: '#10b981',
  600: '#059669',
  700: '#047857',
  900: '#064e3b',
};

// Shared section look (same as WellnessDetail)
const CARD = 'bg-white rounded-3xl border border-gray-100 shadow-sm p-6 lg:p-10';
const H2 = 'text-2xl lg:text-3xl font-serif font-bold text-emerald-900';

function calculateHotelTotalPrice(minPrice, hotelPricing, pricingLoading) {
  if (!hotelPricing || pricingLoading) return {
    totalPrice: minPrice,
    platformAmount: 0,
    fixedFees: 0,
    breakdown: { basePrice: minPrice }
  };

  const {
    platform_price = 0,
    miscellaneous_charges = 0,
    additional_charges_one = 0,
    additional_charges_two = 0,
  } = hotelPricing;

  const platformAmount = (minPrice * platform_price) / 100;
  const fixedFees = miscellaneous_charges + additional_charges_one + additional_charges_two;
  const totalFees = platformAmount + fixedFees;
  const totalPrice = minPrice + totalFees;

  return {
    totalPrice,
    platformAmount,
    fixedFees,
    breakdown: {
      basePrice: minPrice,
      platformAmount,
      miscellaneous_charges,
      additional_charges_one,
      additional_charges_two,
    },
  };
}

const amenityIcons = {
  WiFi: Wifi,
  'Free WiFi': Wifi,
  Parking: Car,
  'Free Parking': Car,
  Restaurant: UtensilsCrossed,
  Dining: UtensilsCrossed,
  Gym: Dumbbell,
  'Fitness Center': Dumbbell,
  'Swimming Pool': Waves,
  Pool: Waves,
  Breakfast: Coffee,
  Farm: TreePine,
  Garden: TreePine,
};

const PD_MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const PD_DAY_NAMES = ['Su','Mo','Tu','We','Th','Fr','Sa'];
const pdDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const pdFirstDayOfWeek = (year, month) => new Date(year, month, 1).getDay();

const formatDateShort = (date) => {
  if (!date) return '';
  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const y = date.getFullYear();
  return `${d}/${m}/${y}`;
};

const RoomCalendarPopover = ({ selectedDate, viewMonth, viewYear, onPrevMonth, onNextMonth, onSelect, minDate, minOffsetDays = 0, }) => {
  const daysInMonth = pdDaysInMonth(viewYear, viewMonth);
  const firstDay = pdFirstDayOfWeek(viewYear, viewMonth);
  const floor = minDate ? new Date(minDate) : new Date();
  floor.setHours(0, 0, 0, 0);
  floor.setDate(floor.getDate() + minOffsetDays);
  const todayFloor = new Date();
  todayFloor.setHours(0, 0, 0, 0);

  const cells = [
    ...Array(firstDay).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  return (
    <div className="w-64 bg-white rounded-lg shadow-2xl border border-slate-100 p-2.5" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-2">
        <button type="button" onClick={onPrevMonth} className="w-6 h-6 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-600 font-bold text-sm">‹</button>
        <span className="text-xs font-semibold text-slate-800">{PD_MONTH_NAMES[viewMonth]} {viewYear}</span>
        <button type="button" onClick={onNextMonth} className="w-6 h-6 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-600 font-bold text-sm">›</button>
      </div>

      <div className="grid grid-cols-7 gap-0.5 mb-0.5">
        {PD_DAY_NAMES.map((d) => (
          <div key={d} className="text-center text-[10px] font-semibold text-slate-400 py-0.5">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((d, i) => {
          if (d === null) return <div key={`e-${i}`} />;
          const cellDate = new Date(viewYear, viewMonth, d);
          cellDate.setHours(0, 0, 0, 0);
          const isPast = cellDate < floor;
          const isToday = cellDate.getTime() === todayFloor.getTime();
          const isSelected =
            selectedDate &&
            selectedDate.getFullYear() === viewYear &&
            selectedDate.getMonth() === viewMonth &&
            selectedDate.getDate() === d;

          return (
            <button
              key={d}
              type="button"
              disabled={isPast}
              onClick={() => onSelect(cellDate)}
              className={`h-7 rounded-md text-xs font-medium transition-colors
                ${isPast ? 'text-slate-300 cursor-not-allowed' : 'text-slate-700 hover:bg-emerald-50'}
                ${isSelected ? '!bg-emerald-600 !text-white' : ''}
                ${isToday && !isSelected ? 'border border-emerald-400' : ''}`}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* Label + read-only value that opens a calendar popover */
const DateField = ({ label, value, open, onToggle, onClose, children }) => (
  <div className="relative flex-1 min-w-0">
    <span className="absolute -top-2 left-3 px-1 bg-white text-[11px] text-gray-500 z-[1]">{label}</span>
    <button
      type="button"
      onClick={onToggle}
      className="w-full h-[42px] px-3 text-left text-sm rounded-lg border border-gray-300 hover:border-emerald-500 transition bg-white"
    >
      {value || <span className="text-gray-400">dd/mm/yyyy</span>}
    </button>
    {open && (
      <>
        <div onClick={onClose} className="fixed inset-0 z-[50]" />
        <div className="absolute z-[60] top-full left-0 mt-2">{children}</div>
      </>
    )}
  </div>
);

/* ───────────────────────── Hero ───────────────────────── */

const ShareSaveButtons = ({ isFavorite, onShare, onToggleFavorite }) => (
  <div className="flex items-center gap-2">
    <button
      onClick={onShare}
      className="flex items-center gap-1.5 bg-white/90 backdrop-blur hover:bg-white text-gray-800 text-xs font-semibold px-3 py-1.5 rounded-full border border-gray-200 shadow-sm transition"
    >
      <Share2 size={14} /> Share
    </button>
    <button
      onClick={onToggleFavorite}
      className="flex items-center gap-1.5 bg-white/90 backdrop-blur hover:bg-white text-gray-800 text-xs font-semibold px-3 py-1.5 rounded-full border border-gray-200 shadow-sm transition"
    >
      <Heart size={14} fill={isFavorite ? '#f43f5e' : 'none'} color={isFavorite ? '#f43f5e' : 'currentColor'} /> Save
    </button>
  </div>
);

/* Badge + title + meta row. `onImage` = white text version used on the desktop hero overlay. */
const HeroMeta = ({ property, onImage = false }) => (
  <div className={onImage ? 'text-white max-w-2xl' : 'max-w-4xl'}>
    <div className="flex flex-wrap gap-2 mb-4">
      {property?.propertyType && (
        <span className="bg-emerald-500 text-white px-3 py-1 rounded-full text-xs font-bold tracking-wider uppercase shadow-sm">
          {property.propertyType}
        </span>
      )}
    </div>

    <h1
      className={`font-serif font-bold leading-tight ${
        onImage ? 'text-4xl lg:text-5xl text-white mb-3' : 'text-3xl sm:text-4xl text-emerald-900 mb-4'
      }`}
    >
      {property?.propertyName || ''}
    </h1>

    {property?.location?.city && (
      <div className={`flex items-center gap-2 text-sm ${onImage ? 'text-slate-200' : 'text-slate-600'}`}>
        <MapPin size={18} className={onImage ? 'text-emerald-300' : 'text-emerald-600'} />
        <span>
          {property.location.city}
          {property.location.state ? `, ${property.location.state}` : ''}
        </span>
      </div>
    )}
  </div>
);

const PropertyImageGallery = ({ images, title, overlay, actions, fallback }) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [openGallery, setOpenGallery] = useState(false);

  if (images.length === 0) {
    return (
      <>
        <NoImage type="hotel" sx={{ height: { xs: 260, md: 500 }, borderRadius: 4 }} />
        {fallback && <div className="hidden md:block mt-6">{fallback}</div>}
      </>
    );
  }

  const cur = selectedImage < images.length ? selectedImage : 0;
  const prev = () => setSelectedImage((i) => (i - 1 + images.length) % images.length);
  const next = () => setSelectedImage((i) => (i + 1) % images.length);

  return (
    <>
      {/* Desktop hero with title overlay */}
      <div className="hidden md:block relative h-[500px] rounded-3xl overflow-hidden shadow-lg">
        <div className="absolute inset-0 cursor-pointer" onClick={() => setOpenGallery(true)}>
          <SafeImage src={images[cur]} type="hotel" alt={title} />
        </div>

        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent pointer-events-none" />

        <div className="absolute top-4 left-4 z-10">{actions}</div>

        {images.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white/90 backdrop-blur rounded-full p-2 shadow-md hover:scale-110 transition"
            >
              <ChevronLeft className="w-5 h-5 text-gray-800" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white/90 backdrop-blur rounded-full p-2 shadow-md hover:scale-110 transition"
            >
              <ChevronRight className="w-5 h-5 text-gray-800" />
            </button>
            <button
              onClick={() => setOpenGallery(true)}
              className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/70 text-white text-xs font-semibold px-3 py-1.5 rounded-full backdrop-blur transition"
            >
              View all {images.length} photos
            </button>
          </>
        )}

        <div className="absolute inset-x-0 bottom-0 p-8 lg:p-10 text-white pointer-events-none">{overlay}</div>
      </div>

      {/* Mobile main image */}
      <div className="md:hidden relative w-full h-[300px] rounded-2xl overflow-hidden">
        <div className="absolute inset-0" onClick={() => setOpenGallery(true)}>
          <SafeImage src={images[cur]} type="hotel" alt={title} />
        </div>
        {images.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur rounded-full p-2 shadow-md active:scale-95"
            >
              <ChevronLeft className="w-5 h-5 text-gray-800" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur rounded-full p-2 shadow-md active:scale-95"
            >
              <ChevronRight className="w-5 h-5 text-gray-800" />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1 rounded-full">
              {cur + 1} / {images.length}
            </div>
          </>
        )}
      </div>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="flex gap-2 md:gap-3 mt-2 md:mt-4 overflow-x-auto scrollbar-hide p-1">
          {images.map((img, idx) => (
            <div
              key={idx}
              onClick={() => setSelectedImage(idx)}
              className={`flex-shrink-0 w-[80px] h-[60px] md:w-[120px] md:h-[84px] rounded-lg md:rounded-xl overflow-hidden cursor-pointer transition-all duration-300
                ${cur === idx ? 'ring-2 ring-emerald-500 scale-105' : 'ring-2 ring-transparent opacity-60 hover:opacity-100'}`}
            >
              <SafeImage src={img} type="hotel" alt={`${title} ${idx + 1}`} compact />
            </div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {openGallery && (
        <div
          className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center"
          onClick={() => setOpenGallery(false)}
        >
          <button
            onClick={() => setOpenGallery(false)}
            className="absolute top-4 right-4 text-white bg-white/20 hover:bg-white/30 rounded-full p-2 z-10"
          >
            <X className="w-6 h-6" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white bg-white/20 hover:bg-white/30 rounded-full p-3 z-10"
          >
            <ChevronLeft className="w-7 h-7" />
          </button>

          <div onClick={(e) => e.stopPropagation()}>
            <SafeImage
              src={images[cur]} type="hotel" alt={title}
              sx={{ width: 'auto', height: 'auto', maxHeight: '90vh', maxWidth: '90vw', minWidth: 200, minHeight: 120, objectFit: 'contain', borderRadius: 3 }}
            />
          </div>

          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white bg-white/20 hover:bg-white/30 rounded-full p-3 z-10"
          >
            <ChevronRight className="w-7 h-7" />
          </button>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white text-sm font-medium bg-black/50 px-4 py-1.5 rounded-full">
            {cur + 1} / {images.length}
          </div>
        </div>
      )}
    </>
  );
};

/* ───────────────────────── Page ───────────────────────── */

export function PropertyDetail({ property: propFromParent, onBack, onBookNow }) {
  const navigate = useNavigate();
  const bookingCardRef = useRef(null);
  const [showStickyBar, setShowStickyBar] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [hotelPricing, setHotelPricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [checkoutEventId, setCheckoutEventId] = useState(null);

  // Auto-select lowest priced accommodation
  const getLowestPricedAccommodation = (accommodations) => {
    if (!Array.isArray(accommodations) || accommodations.length === 0) return null;
    return accommodations.reduce((lowest, current) => {
      const lowestPrice = lowest?.pricePerNightB2C ?? Infinity;
      const currentPrice = current?.pricePerNightB2C ?? Infinity;
      return currentPrice < lowestPrice ? current : lowest;
    }, accommodations[0]);
  };

  const [selectedAccommodation, setSelectedAccommodation] = useState(null);
  const { propertyId } = useParams();
  const [property, setProperty] = useState(null);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [serviceTerms, setServiceTerms] = useState('');

  const [selectedMealPlan, setSelectedMealPlan] = useState('EP');

  function getMetaCookie(name) {
    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
    return match ? match[2] : null;
  }

  useEffect(() => {
    async function fetchProperty() {
      try {
        const fbp = getMetaCookie('_fbp');
        const fbc = getMetaCookie('_fbc');

        const res = await axios.get(
          `${KeyUri.BACKEND_URI}/properties-user/${propertyId}`,
          { params: { fbp, fbc } }
        );
        setProperty(res.data.data);

        trackEvent('ViewContent', {
          content_ids: [res.data.data._id],
          content_type: 'hotel_room',
          content_name: res.data.data.propertyName,
          value: Number(res.data.data.basePrice || 0),
          currency: 'INR',
        }, res.data.viewEventId);
      } catch (err) {
        console.error('Failed to load property', err);
      }
    }
    fetchProperty();
  }, [propertyId]);

  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [availability, setAvailability] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [openBookingModal, setOpenBookingModal] = useState(false);

  const [couponCode, setCouponCode] = useState('');
  const [couponData, setCouponData] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [couponError, setCouponError] = useState('');

  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [couponModalOpen, setCouponModalOpen] = useState(false);
  const [couponLoading, setCouponLoading] = useState(false);

  const [roomCalOpen, setRoomCalOpen] = useState(null); // 'checkin' | 'checkout' | null
  const [roomCheckInView, setRoomCheckInView] = useState({ month: new Date().getMonth(), year: new Date().getFullYear() });
  const [roomCheckOutView, setRoomCheckOutView] = useState({ month: new Date().getMonth(), year: new Date().getFullYear() });

  useEffect(() => {
    const el = bookingCardRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyBar(!entry.isIntersecting),
      { root: null, threshold: 0.2 }
    );
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, []);

  useEffect(() => {
    if (property?.accommodations?.length) {
      const lowest = getLowestPricedAccommodation(property.accommodations);
      setSelectedAccommodation(lowest);
    }
  }, [property]);

  const checkRoomAvailability = async (accommodationId) => {
    if (!checkIn || !checkOut || !accommodationId) {
      setAvailability(null);
      return;
    }

    setAvailabilityLoading(true);
    try {
      const checkInStr = checkIn.toISOString().split('T')[0];
      const checkOutStr = checkOut.toISOString().split('T')[0];

      const res = await axios.get(
        `${KeyUri.BACKEND_URI}/accommodations/${accommodationId}/availability?checkIn=${checkInStr}&checkOut=${checkOutStr}&quantity=${selectedQuantity}`
      );

      setAvailability(res.data);
    } catch (err) {
      console.error('Availability check failed:', err);
      setAvailability({ available: false });
    } finally {
      setAvailabilityLoading(false);
    }
  };

const handleAccommodationSelect = (acc) => {
  setSelectedAccommodation(acc);
  setSelectedMealPlan('EP');
  setSelectedQuantity(1);
  setAvailability(null);
};

  const handleBookNow = () => {
    if (!selectedAccommodation || !availability?.available || !checkIn || !checkOut) {
      alert('Please select room, dates, and confirm availability');
      return;
    }

    if (checkOut <= checkIn) {
      toast.error('Check-out must be at least one day after check-in');
      return;
    }

    setTermsModalOpen(true);
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    const shareData = {
      title: property?.propertyName || 'The Great Himalayas',
      text: `Check out ${property?.propertyName || 'this property'} on The Great Himalayas`,
      url: shareUrl,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
      }
    } catch (err) {
      // user cancelled share sheet — no-op
    }
  };

  const handleToggleFavorite = () => {
    setIsFavorite((prev) => {
      const next = !prev;
      toast.success(next ? 'Added to saved properties!' : 'Removed from saved properties.');
      return next;
    });
  };

  const fetchAvailableCoupons = async () => {
    try {
      setCouponLoading(true);

      const res = await axios.get(`${KeyUri.BACKEND_URI}/hotel-coupons/public`);

      let coupons = res.data?.coupons || res.data?.data || res.data || [];
      if (!Array.isArray(coupons)) coupons = [];

      const today = new Date();

      const filtered = coupons.filter((c) => {
        if (!c.is_active) return false;
        if (c.valid_from && new Date(c.valid_from) > today) return false;
        if (c.valid_until && new Date(c.valid_until) < today) return false;
        if (c.usage_limit && c.used_count >= c.usage_limit) return false;
        if (c.applicable_hotels?.length) {
          return c.applicable_hotels.includes(propertyId);
        }
        return true;
      });

      filtered.sort((a, b) => (b.coupon_amount || 0) - (a.coupon_amount || 0));

      setAvailableCoupons(filtered);
      setCouponModalOpen(true);
    } catch (err) {
      console.error('Hotel coupon fetch error:', err);
      toast.error('Failed to load coupons');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleApplyCoupon = async () => {
    try {
      setCouponError('');

      const tokenUser = localStorage.getItem('tokenUser') || localStorage.getItem('tokenuser') || localStorage.getItem('token');
      if (!tokenUser) {
        return;
      }

      const token = tokenUser.replace(/^"|"$/g, '');
      const userId = JSON.parse(atob(token.split('.')[1]))?.id ||
                    JSON.parse(atob(token.split('.')[1]))?._id;

      const res = await axios.post(
        `${KeyUri.BACKEND_URI}/hotel-coupon/validate`,
        {
          coupon_code: couponCode,
          propertyId: propertyId,
          bookingAmount: totalBookingAmount || selectedAccommodation?.pricePerNightB2C || 0,
          checkinDate: checkIn ? checkIn.toISOString().split('T')[0] : '',
          userId: userId,
          participants: selectedQuantity
        },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      const data = res.data;
      setCouponData(data.coupon);
      setDiscountAmount(data.discount);
      toast.success(`Coupon applied! Saved ₹${data.discount}`);
    } catch (err) {
      setCouponError(err.response?.data?.message || 'Invalid coupon');
      setCouponData(null);
      setDiscountAmount(0);
      toast.error(err.response?.data?.message || 'Invalid coupon');
    }
  };

  const handleSelectCoupon = async (coupon) => {
    setCouponCode(coupon.coupon_code);
    setCouponModalOpen(false);

    try {
      const userId = JSON.parse(localStorage.getItem('userID'));

      const res = await axios.post(
        `${KeyUri.BACKEND_URI}/hotel-coupon/validate`,
        {
          coupon_code: coupon.coupon_code,
          propertyId: propertyId,
          bookingAmount: totalBookingAmount || selectedAccommodation?.pricePerNightB2C || 0,
          checkinDate: checkIn ? checkIn.toISOString().split('T')[0] : '',
          userId: userId,
          participants: selectedQuantity
        }
      );

      setCouponData(res.data.coupon);
      setDiscountAmount(res.data.discount);
      setCouponError('');
      toast.success(`Coupon applied! Saved ₹${res.data.discount}`);
    } catch (err) {
      setCouponError(err.response?.data?.message || 'Invalid coupon');
      setCouponData(null);
      setDiscountAmount(0);
    }
  };

  useEffect(() => {
    if (selectedAccommodation?._id && checkIn && checkOut) {
      checkRoomAvailability(selectedAccommodation._id);
    }
  }, [selectedAccommodation, checkIn, checkOut, selectedQuantity]);

  // Defensive access to accommodations and prices
  const accommodations = property?.accommodations || [];
  const allPrices = accommodations
    .map(a => (typeof a?.pricePerNightB2C === 'number' ? a.pricePerNightB2C : null))
    .filter(n => typeof n === 'number' && n > 0);

  const minPrice = allPrices.length > 0 ? Math.min(...allPrices) : null;

  const images = normalizeImages(property?.images);

  // Defensive amenities flattening
  const allAmenities = [
    ...(property?.amenities?.general || []),
    ...(property?.amenities?.recreational || []),
    ...(property?.amenities?.dining || []),
    ...(property?.amenities?.services || [])
  ];

  useEffect(() => {
    async function fetchHotelPricing() {
      try {
        const res = await axios.get(`${KeyUri.BACKEND_URI}/get-hotelpricing`);
        setHotelPricing(res.data.data);
      } catch (err) {
        console.error('Failed to fetch hotel pricing:', err);
        setHotelPricing(null);
      } finally {
        setPricingLoading(false);
      }
    }

    fetchHotelPricing();
  }, []);

  useEffect(() => {
    const fetchServiceTerms = async () => {
      try {
        const res = await axios.get(`${KeyUri.BACKEND_URI}/terms/service-user-terms`);
        setServiceTerms(res.data.serviceUserTerms || '');
      } catch (error) {
        console.error('Failed to fetch terms:', error);
      }
    };
    fetchServiceTerms();
  }, []);

  // Calculate total price using same logic as PropertyCard
  const pricingResult = calculateHotelTotalPrice(minPrice, hotelPricing, pricingLoading);
  const { totalPrice, platformAmount, fixedFees, breakdown } = pricingResult;

  const getAccommodationTotalPrice = (roomPrice) => {
    if (typeof roomPrice !== 'number' || !hotelPricing || pricingLoading) {
      return roomPrice;
    }
    return calculateHotelTotalPrice(roomPrice, hotelPricing, pricingLoading).totalPrice;
  };

  const nights = selectedAccommodation && checkIn && checkOut
    ? Math.max(1, Math.round((checkOut - checkIn) / (1000 * 60 * 60 * 24)))
    : 1;

  const getEPOrMealPlanPrice = (acc, mealPlan) => {
    if (!acc) return 0;
    if (mealPlan === 'EP') return acc.pricePerNightB2C;
    return acc.mealPlanPricing?.[mealPlan]?.B2C || acc.pricePerNightB2C;
  };

  const roomPrice = getAccommodationTotalPrice(
    getEPOrMealPlanPrice(selectedAccommodation, selectedMealPlan)
  );
  const baseTotal = roomPrice * selectedQuantity * nights;
  const gstRate = (hotelPricing?.gst_price || 0) / 100;
  const gstAmount = Math.round(baseTotal * gstRate);
  const totalBeforeDiscount = Math.round(baseTotal * (1 + gstRate));
  const totalBookingAmount = Math.max(0, totalBeforeDiscount - discountAmount);

  const unitLabel = selectedAccommodation?.type === 'dormitory' ? 'bed' : 'room';
  const maxQty = selectedAccommodation?.type === 'dormitory' ? 24 : 10;

  const actionButtons = (
    <ShareSaveButtons isFavorite={isFavorite} onShare={handleShare} onToggleFavorite={handleToggleFavorite} />
  );

  return (
    <div className="min-h-screen bg-slate-50 pb-24 lg:pb-0 overflow-x-clip">
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-2 md:ml-8 pt-24 md:pt-40">
        <button
          onClick={() => { if (onBack) onBack(); else navigate(-1); }}
          className="flex items-center gap-2 text-emerald-700 font-medium hover:text-emerald-900 transition"
        >
          <ArrowLeft size={18} /> Back
        </button>
      </div>

      {/* Main layout: left = story (hero image first), right = booking card beside the image */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 pt-4 pb-8 mt-2">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 min-w-0">
          <div className="lg:col-span-7 xl:col-span-8 space-y-8 lg:space-y-12 min-w-0">

            {/* 1. HERO */}
            <section id="hero">
              <PropertyImageGallery
                images={images}
                title={property?.propertyName || ''}
                actions={actionButtons}
                overlay={<HeroMeta property={property} onImage />}
                fallback={<HeroMeta property={property} />}
              />

              {/* Mobile-only title block below the image */}
              <div className="md:hidden mt-6">
                <HeroMeta property={property} />
                <div className="mt-4">{actionButtons}</div>
              </div>
            </section>

            {/* 2. Highlights */}
            <div className={CARD}>
              {property?.location?.address && (
                <div className="flex items-start gap-2 text-sm text-gray-600 mb-6">
                  <MapPin size={16} className="mt-0.5 flex-shrink-0" />
                  <span>
                    {property.location.address}
                    {property.location.city ? `, ${property.location.city}` : ''}
                    {property.location.state ? `, ${property.location.state}` : ''}
                  </span>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {(property?.checkInTime || property?.policies?.checkInTime) && (
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600 shadow-sm">
                      <Check size={22} />
                    </div>
                    <div className="self-center">
                      <p className="text-sm font-bold text-slate-800">Flexible check-in</p>
                      <p className="text-xs text-gray-500">From {property?.checkInTime || property?.policies?.checkInTime}</p>
                    </div>
                  </div>
                )}
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600 shadow-sm">
                    <MapPin size={22} />
                  </div>
                  <div className="self-center">
                    <p className="text-sm font-bold text-slate-800">Great location</p>
                    <p className="text-xs text-gray-500">
                      {`Rated ${property?.averageRating || 0} by recent guests`}
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600 shadow-sm">
                    <HomeIcon size={22} />
                  </div>
                  <div className="self-center">
                    <p className="text-sm font-bold text-slate-800">{property?.propertyType || 'Verified stay'}</p>
                    <p className="text-xs text-gray-500">
                      {accommodations.length} room type{accommodations.length !== 1 ? 's' : ''} available
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 3. About */}
            <div className={CARD}>
              <h2 className={`${H2} mb-6`}>About this property</h2>
              <div
                className="text-gray-700 leading-relaxed prose max-w-none"
                dangerouslySetInnerHTML={{ __html: property?.description || '' }}
              />
            </div>

            {/* 4. Amenities */}
            {(
              <div className={CARD}>
                <h2 className={`${H2} mb-8`}>What this place offers</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {allAmenities.map((name, idx) => {
                    const Icon = amenityIcons[name] || HomeIcon;
                    return (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600">
                          <Icon size={18} />
                        </div>
                        <span className="text-sm font-medium text-slate-700">{name}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 5. Accommodations */}
            <div id="accommodations" className={`${CARD} scroll-mt-24`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className={H2}>Available Accommodations</h2>
                {selectedAccommodation && (
                  <span className="text-sm font-bold text-emerald-600">1 Room Selected</span>
                )}
              </div>

              <div className="space-y-4">
                {accommodations.map((acc, idx) => {
const isSelected = selectedAccommodation === acc;
const isDorm = acc.type === 'dormitory';
const qty = isSelected ? selectedQuantity : 1;
                  return (
                    <div
                      key={idx}
                      onClick={() => handleAccommodationSelect(acc)}
                      className={`rounded-2xl border-2 p-4 cursor-pointer transition-colors
                        ${isSelected ? 'border-emerald-600 bg-emerald-50/40' : 'border-gray-200 hover:border-emerald-300'}`}
                    >
                      <div className="grid md:grid-cols-[220px_1fr] gap-4">
                        {/* Image */}
                        <div className="relative h-40 w-full rounded-xl overflow-hidden">
                          <SafeImage src={pickImage(acc?.images)} type="hotel" alt={acc?.name || ''} />
                          {acc?.type && (
                            <span className="absolute top-2.5 left-2.5 bg-black/65 text-white text-[11px] font-semibold px-2.5 py-1 rounded-md capitalize backdrop-blur-sm">
                              {acc.type}
                            </span>
                          )}
                        </div>

                        {/* Details */}
                        <div className="min-w-0">
                          <div className="flex flex-wrap justify-between items-start gap-2 mb-2">
                            <h3 className="text-lg font-bold text-slate-900 flex-1 min-w-[120px]">{acc?.name || 'Room'}</h3>
                            <div className="text-right flex-shrink-0">
                              <p className="text-lg font-extrabold text-emerald-700">
                                ₹{(getAccommodationTotalPrice(acc?.pricePerNightB2C) * qty).toLocaleString('en-IN')}
                              </p>
                              <p className="text-xs text-gray-500">
                           {isDorm
  ? `per bed × ${qty} bed${qty > 1 ? 's' : ''}`
  : `per night × ${qty} room${qty > 1 ? 's' : ''}`}
                              </p>
                            </div>
                          </div>

                          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-x-4 gap-y-1 text-gray-500 mb-2">
                            {acc?.bedConfiguration && (
                              <span className="flex items-center gap-1.5 text-xs">
                                <BedDouble size={14} /> {acc.bedConfiguration}
                              </span>
                            )}
                            <span className="flex items-center gap-1.5 text-xs">
                              <Users size={14} />
                              {isDorm
                                ? `${acc.bedsPerRoom || 8} Beds/Room • ${acc.quantity || 0} Rooms • ${acc.genderRestriction || 'Mixed'}`
                                : `Up to ${acc.maxOccupancy} guests`}
                            </span>
                            {acc?.size?.area > 0 && (
                              <span className="flex items-center gap-1.5 text-xs">
                                <HomeIcon size={14} /> {acc.size.width} × {acc.size.length} ft ({acc.size.area} sq ft)
                              </span>
                            )}
                          </div>

                       {isSelected && (
  <span
    className={`inline-block mt-1 px-2 py-1 rounded-md border text-xs font-medium
      ${availability?.available
        ? 'bg-green-100 border-green-200 text-green-800'
        : 'bg-red-50 border-red-200 text-red-700'}`}
  >
    {availabilityLoading ? 'Checking...' :
     !availability ? 'Select dates' :
     availability.available
       ? `${availability.availableQuantity} ${availability.type === 'dormitory' ? 'beds' : 'rooms'} / ${availability.totalQuantity} available`
       : `Only ${availability.availableQuantity} ${availability.type === 'dormitory' ? 'beds' : 'rooms'} left`}
  </span>
)}

                          {!isSelected && (
                            <div className="mt-3">
                              <button
                                onClick={(e) => { e.stopPropagation(); handleAccommodationSelect(acc); }}
                                className="text-sm font-semibold text-emerald-700 border border-emerald-600 hover:bg-emerald-50 px-4 py-1.5 rounded-lg transition"
                              >
                                Select This Room
                              </button>
                            </div>
                          )}

                          {/* Dates + quantity */}
                          {isSelected && (
                            <div className="flex flex-col sm:flex-row gap-3 mt-5" onClick={(e) => e.stopPropagation()}>
                              <DateField
                                label="Check-in"
                                value={checkIn ? formatDateShort(checkIn) : ''}
                                open={roomCalOpen === 'checkin'}
                                onToggle={() => setRoomCalOpen((o) => (o === 'checkin' ? null : 'checkin'))}
                                onClose={() => setRoomCalOpen(null)}
                              >
                                <RoomCalendarPopover
                                  selectedDate={checkIn}
                                  viewMonth={roomCheckInView.month}
                                  viewYear={roomCheckInView.year}
                                  onPrevMonth={() =>
                                    setRoomCheckInView((v) =>
                                      v.month === 0 ? { month: 11, year: v.year - 1 } : { month: v.month - 1, year: v.year }
                                    )
                                  }
                                  onNextMonth={() =>
                                    setRoomCheckInView((v) =>
                                      v.month === 11 ? { month: 0, year: v.year + 1 } : { month: v.month + 1, year: v.year }
                                    )
                                  }
                                  onSelect={(date) => {
                                    setCheckIn(date);
                                    if (checkOut && date >= checkOut) setCheckOut(null);
                                  //  if (selectedAccommodation) checkRoomAvailability(selectedAccommodation._id);
                                    setRoomCalOpen(null);
                                  }}
                                />
                              </DateField>

                              <DateField
                                label="Check-out"
                                value={checkOut ? formatDateShort(checkOut) : ''}
                                open={roomCalOpen === 'checkout'}
                                onToggle={() => setRoomCalOpen((o) => (o === 'checkout' ? null : 'checkout'))}
                                onClose={() => setRoomCalOpen(null)}
                              >
                                <RoomCalendarPopover
                                  selectedDate={checkOut}
                                  viewMonth={roomCheckOutView.month}
                                  viewYear={roomCheckOutView.year}
                                  minDate={checkIn || new Date()}
                                  minOffsetDays={checkIn ? 1 : 0}
                                  onPrevMonth={() =>
                                    setRoomCheckOutView((v) =>
                                      v.month === 0 ? { month: 11, year: v.year - 1 } : { month: v.month - 1, year: v.year }
                                    )
                                  }
                                  onNextMonth={() =>
                                    setRoomCheckOutView((v) =>
                                      v.month === 11 ? { month: 0, year: v.year + 1 } : { month: v.month + 1, year: v.year }
                                    )
                                  }
                                  onSelect={(date) => {
                                    setCheckOut(date);
                                   // if (selectedAccommodation) checkRoomAvailability(selectedAccommodation._id);
                                    setRoomCalOpen(null);
                                  }}
                                />
                              </DateField>

                              <div className="relative w-full sm:w-[150px]">
                                <span className="absolute -top-2 left-3 px-1 bg-white text-[11px] text-gray-500 z-[1]">
                                  {selectedAccommodation?.type === 'dormitory' ? 'Beds' : 'Rooms'}
                                </span>
                                <div className="flex items-center h-[42px] border border-gray-300 rounded-lg overflow-hidden bg-white">
                                  <button
                                    type="button"
                                    onClick={() => setSelectedQuantity((q) => Math.max(1, q - 1))}
                                    disabled={selectedQuantity <= 1}
                                    className="h-full w-10 font-bold text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                                  >
                                    −
                                  </button>
                                  <span className="flex-1 h-full flex items-center justify-center text-sm font-semibold border-x border-gray-200">
                                    {selectedQuantity}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => setSelectedQuantity((q) => Math.min(maxQty, q + 1))}
                                    disabled={selectedQuantity >= maxQty}
                                    className="h-full w-10 font-bold text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                                  >
                                    +
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Meal plans */}
                          {isSelected && (
                            <div className="mt-5" onClick={(e) => e.stopPropagation()}>
                              <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 mb-2">
                                <Check size={14} className="text-emerald-600" /> Select Plan
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {[
                                  { key: 'EP',  label: 'Room Only' },
                                  { key: 'CP',  label: 'Room + Breakfast' },
                                  { key: 'MAP', label: 'Room + Breakfast & Dinner' },
                                  { key: 'AP',  label: 'Room + All Meals' },
                                ].map(({ key, label }) => {
                                  const rawPrice = key === 'EP'
                                    ? acc.pricePerNightB2C
                                    : acc.mealPlanPricing?.[key]?.B2C;

                                  if (!rawPrice) return null;

                                  const displayPrice = getAccommodationTotalPrice(rawPrice);
                                  const active = selectedMealPlan === key;

                                  return (
                                    <button
                                      type="button"
                                      key={key}
                                      onClick={(e) => { e.stopPropagation(); setSelectedMealPlan(key); }}
                                      className={`text-left px-3 py-2 rounded-xl border-2 transition flex-1 min-w-[45%] sm:flex-none sm:min-w-0
                                        ${active ? 'border-emerald-600 bg-emerald-50' : 'border-gray-200 hover:border-emerald-400'}`}
                                    >
                                      <span className="block text-[11px] text-gray-500">{label}</span>
                                      <span className="block text-sm font-semibold text-emerald-700">
                                        ₹{displayPrice?.toLocaleString('en-IN')}/night
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 6. Location & Map — appearance intentionally unchanged */}
            {property?.location && (
              <div className="[&>.MuiCard-root]:!my-0">
                <Card sx={{ p: 3, mb: 3, mt: 3, borderRadius: 4, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'stretch', sm: 'center' },
                      justifyContent: 'space-between',
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <MapPin size={22} color={BRAND[600]} />
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>Location</Typography>
                    </Stack>

                    {property.location.mapShareLink && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<MapPin size={16} />}
                        href={property.location.mapShareLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          width: { xs: '100%', sm: 'auto' },
                          justifyContent: 'center',
                          backgroundColor: BRAND[600],
                          '&:hover': { backgroundColor: BRAND[700] },
                          textTransform: 'none',
                          fontWeight: 600,
                          borderRadius: 2,
                          py: { xs: 1, sm: 0.5 },
                        }}
                      >
                        Open in Maps
                      </Button>
                    )}
                  </Box>

                  {(property.location.address || property.location.city) && (
                    <Box
                      sx={{
                        width: '100%',
                        height: 280,
                        borderRadius: 3,
                        overflow: 'hidden',
                        border: '1px solid #e5e7eb',
                        mb: 2,
                      }}
                    >
                      <iframe
                        title="property-location-map"
                        width="100%"
                        height="100%"
                        frameBorder="0"
                        scrolling="no"
                        style={{ border: 0 }}
                        src={`https://maps.google.com/maps?q=${encodeURIComponent(
                          [property.location.address, property.location.city, property.location.state, property.location.pincode]
                            .filter(Boolean)
                            .join(', ')
                        )}&t=&z=14&ie=UTF8&iwloc=&output=embed`}
                      />
                    </Box>
                  )}

                  <Box sx={{ p: 2, bgcolor: BRAND[50], borderRadius: 2, border: `1px solid ${BRAND[100]}` }}>
                    <Typography variant="body1" fontWeight={600} gutterBottom>
                      {property.location.address}
                    </Typography>
                    <Typography variant="body2" sx={{ color: BRAND[600] }} fontWeight={500}>
                      {property.location.city}, {property.location.state} - {property.location.pincode}
                    </Typography>
                  </Box>

                  {property?.location?.how_to_reach && (
                    <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #f1f5f9' }}>
                      <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
                        How To Reach
                      </Typography>

                      {property.location.how_to_reach.overview && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 3, lineHeight: 1.7 }}
                        >
                          {property.location.how_to_reach.overview}
                        </Typography>
                      )}

                      <Stack spacing={2}>
                        {/* By Air */}
                        {property.location.how_to_reach.by_air?.airport_name && (
                          <Box
                            sx={{
                              display: "flex",
                              gap: 2,
                              p: 2,
                              bgcolor: "#f9fafb",
                              borderRadius: 2,
                              border: "1px solid #f3f4f6",
                            }}
                          >
                            <Box
                              sx={{
                                width: 42,
                                height: 42,
                                borderRadius: "50%",
                                bgcolor: "#e0f2fe",
                                color: "#0284c7",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <Plane size={20} strokeWidth={2} aria-hidden="true" />
                            </Box>

                            <Box>
                              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                By Air
                              </Typography>
                              <Typography variant="body2" color="text.primary">
                                <strong>{property.location.how_to_reach.by_air.airport_name}</strong>

                                {property.location.how_to_reach.by_air.airport_code && (
                                  <span style={{ color: "#6b7280", marginLeft: 4 }}>
                                    ({property.location.how_to_reach.by_air.airport_code})
                                  </span>
                                )}

                                {property.location.how_to_reach.by_air.distance_label && (
                                  <span style={{ color: BRAND[600], fontWeight: 500, marginLeft: 8 }}>
                                    — {property.location.how_to_reach.by_air.distance_label}
                                  </span>
                                )}

                                {property.location.how_to_reach.by_air.distance_km != null && (
                                  <span style={{ color: "#6b7280", marginLeft: 8 }}>
                                    ({property.location.how_to_reach.by_air.distance_km} km)
                                  </span>
                                )}
                              </Typography>
                              {property.location.how_to_reach.by_air.instructions && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                  {property.location.how_to_reach.by_air.instructions}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        )}

                        {/* By Train */}
                        {property.location.how_to_reach.by_train?.station_name && (
                          <Box
                            sx={{
                              display: "flex",
                              gap: 2,
                              p: 2,
                              bgcolor: "#f9fafb",
                              borderRadius: 2,
                              border: "1px solid #f3f4f6",
                            }}
                          >
                            <Box
                              sx={{
                                width: 42,
                                height: 42,
                                borderRadius: "50%",
                                bgcolor: "#ede9fe",
                                color: "#7c3aed",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <TrainFront size={20} strokeWidth={2} aria-hidden="true" />
                            </Box>

                            <Box>
                              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                By Train
                              </Typography>
                              <Typography variant="body2" color="text.primary">
                                <strong>{property.location.how_to_reach.by_train.station_name}</strong>

                                {property.location.how_to_reach.by_train.station_code && (
                                  <span style={{ color: "#6b7280", marginLeft: 4 }}>
                                    ({property.location.how_to_reach.by_train.station_code})
                                  </span>
                                )}

                                {property.location.how_to_reach.by_train.distance_label && (
                                  <span style={{ color: BRAND[600], fontWeight: 500, marginLeft: 8 }}>
                                    — {property.location.how_to_reach.by_train.distance_label}
                                  </span>
                                )}

                                {property.location.how_to_reach.by_train.distance_km != null && (
                                  <span style={{ color: "#6b7280", marginLeft: 8 }}>
                                    ({property.location.how_to_reach.by_train.distance_km} km)
                                  </span>
                                )}
                              </Typography>
                              {property.location.how_to_reach.by_train.instructions && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                  {property.location.how_to_reach.by_train.instructions}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        )}

                        {/* By Road */}
                        {(property.location.how_to_reach.by_road?.bus_stand ||
                          property.location.how_to_reach.by_road?.distance_label ||
                          property.location.how_to_reach.by_road?.instructions) && (
                          <Box
                            sx={{
                              display: "flex",
                              gap: 2,
                              p: 2,
                              bgcolor: "#f9fafb",
                              borderRadius: 2,
                              border: "1px solid #f3f4f6",
                            }}
                          >
                            <Box
                              sx={{
                                width: 42,
                                height: 42,
                                borderRadius: "50%",
                                bgcolor: "#fef3c7",
                                color: "#d97706",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <Bus size={20} strokeWidth={2} aria-hidden="true" />
                            </Box>

                            <Box>
                              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                By Road
                              </Typography>
                              {property.location.how_to_reach.by_road.distance_label && (
                                <Typography variant="body2" sx={{ color: BRAND[600] }} fontWeight={500}>
                                  {property.location.how_to_reach.by_road.distance_label}
                                </Typography>
                              )}
                              {property.location.how_to_reach.by_road.bus_stand && (
                                <Typography variant="body2" color="text.primary">
                                  Nearest bus stand:{" "}
                                  <strong>{property.location.how_to_reach.by_road.bus_stand}</strong>
                                </Typography>
                              )}
                              {property.location.how_to_reach.by_road.instructions && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                  {property.location.how_to_reach.by_road.instructions}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        )}

                        {/* Local Transfer */}
                        {property.location.how_to_reach.local_transfer?.available && (
                          <Box
                            sx={{
                              display: "flex",
                              gap: 2,
                              p: 2,
                              bgcolor: BRAND[50],
                              borderRadius: 2,
                              border: `1px solid ${BRAND[100]}`,
                            }}
                          >
                            <Box
                              sx={{
                                width: 42,
                                height: 42,
                                borderRadius: "50%",
                                bgcolor: BRAND[100],
                                color: "#15803d",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <Truck size={20} strokeWidth={2} aria-hidden="true" />
                            </Box>

                            <Box>
                              <Typography variant="subtitle2" fontWeight={700} color="#065f46" gutterBottom>
                                Local Transfer Available
                              </Typography>
                              {property.location.how_to_reach.local_transfer.details && (
                                <Typography variant="body2" sx={{ color: BRAND[600] }}>
                                  {property.location.how_to_reach.local_transfer.details}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        )}
                      </Stack>
                    </Box>
                  )}
                </Card>
              </div>
            )}

            {/* 7. Policies */}
            <div className={CARD}>
              <h2 className={`${H2} mb-8`}>Policies &amp; House Rules</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-5 rounded-2xl bg-gray-50 border border-gray-100">
                  <p className="flex items-center gap-2 text-sm font-bold text-slate-800 mb-3">
                    <Clock size={16} className="text-emerald-600" /> Check-in / Out
                  </p>
                  <p className="text-sm text-gray-600 mb-1">
                    <strong className="text-slate-800">Check-in: </strong>
                    {property?.checkInTime || property?.policies?.checkInTime || '—'}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong className="text-slate-800">Check-out: </strong>
                    {property?.checkOutTime || property?.policies?.checkOutTime || '—'}
                  </p>
                </div>

                <div className="p-5 rounded-2xl bg-gray-50 border border-gray-100">
                  <p className="flex items-center gap-2 text-sm font-bold text-slate-800 mb-3">
                    <Ban size={16} className="text-emerald-600" /> Cancellation
                  </p>
                  <p className="text-sm text-gray-600">
                    {property?.policies?.cancellationPolicy || property?.policies?.cancellation || 'See booking terms for details.'}
                  </p>
                </div>

                <div className="p-5 rounded-2xl bg-gray-50 border border-gray-100">
                  <p className="flex items-center gap-2 text-sm font-bold text-slate-800 mb-3">
                    <ShieldCheck size={16} className="text-emerald-600" /> Other Policies
                  </p>
                  {property?.policies?.childPolicy && (
                    <p className="text-sm text-gray-600 mb-1">
                      <strong className="text-slate-800">Child Policy: </strong>
                      {property.policies.childPolicy}
                    </p>
                  )}
                  <p className="text-sm text-gray-600">
                    <strong className="text-slate-800">Pet Policy: </strong>
                    {property?.policies?.petPolicy || '—'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ================= RIGHT COLUMN: booking card ================= */}
          <div
            id="booking-card"
            ref={bookingCardRef}
            className="lg:col-span-5 xl:col-span-4 self-start lg:sticky lg:top-24 z-30 min-w-0 scroll-mt-24"
          >
            <div
              className="bg-white rounded-3xl p-5 lg:p-8 shadow-[0_20px_50px_-12px_rgb(0,0,0,0.1)] border border-gray-200
                lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto lg:pr-6
                [scrollbar-width:thin] [scrollbar-color:#d1d5db_transparent]
                [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent
                [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300
                hover:[&::-webkit-scrollbar-thumb]:bg-gray-400"
            >
              {/* Price */}
              {selectedAccommodation && (
                <div className="mb-6 pb-6 border-b border-gray-100">
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-serif font-bold text-emerald-900">
                      ₹{getAccommodationTotalPrice(getEPOrMealPlanPrice(selectedAccommodation, selectedMealPlan))?.toLocaleString('en-IN') || 'N/A'}
                    </span>
                    <span className="text-sm text-slate-500">/ night</span>
                  </div>
                </div>
              )}

              {/* Coupon */}
              <div className="mb-5">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Have a coupon?</p>
                <div className="flex gap-2">
                  <input
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="e.g. HIMALAYAS10"
                    className="flex-1 min-w-0 px-3 py-2.5 text-sm uppercase rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                  />
                  <button
                    onClick={handleApplyCoupon}
                    disabled={!couponCode || couponLoading}
                    className="px-5 text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Apply
                  </button>
                </div>
                <button
                  onClick={fetchAvailableCoupons}
                  className="mt-2 text-xs font-semibold text-emerald-700 hover:underline"
                >
                  {couponLoading ? 'Loading...' : 'View available coupons'}
                </button>
                {couponData && (
                  <p className="text-xs text-green-600 font-semibold mt-2">
                    Coupon applied — saved ₹{discountAmount.toLocaleString('en-IN')}
                  </p>
                )}
                {couponError && <p className="text-xs text-red-500 mt-2">{couponError}</p>}
              </div>

              {/* Stay summary */}
              {selectedAccommodation && (
                <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100 mb-5 space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Dates:</span>
                    <span className="font-bold text-slate-800">
                      {checkIn ? formatDateShort(checkIn) : '—'} - {checkOut ? formatDateShort(checkOut) : '—'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{selectedAccommodation?.type === 'dormitory' ? 'Beds:' : 'Rooms:'}</span>
                    <span className="font-bold text-slate-800">{selectedQuantity}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Nights:</span>
                    <span className="font-bold text-slate-800">{nights || 1}</span>
                  </div>
                </div>
              )}

              {/* Availability */}
              {selectedAccommodation && availability && (
                <div
                  className={`mb-5 p-3 rounded-xl border text-xs font-semibold
                    ${availability.available
                      ? 'bg-green-100 border-green-200 text-green-800'
                      : 'bg-red-50 border-red-200 text-red-700'}`}
                >
                  {availability.available ? 'Available!' : 'Not Available'}
                  {availability.available && ` (${availability.availableQuantity} ${availability.type === 'dormitory' ? 'beds' : 'rooms'} left)`}
                </div>
              )}

              {/* Breakdown */}
              <div className="space-y-3 text-sm">
                <div className="flex justify-between gap-3">
                  <span className="text-gray-500">
                    ₹{getAccommodationTotalPrice(getEPOrMealPlanPrice(selectedAccommodation, selectedMealPlan))?.toLocaleString('en-IN')} × {nights || 1} night{nights > 1 ? 's' : ''} × {selectedQuantity} {unitLabel}{selectedQuantity > 1 ? 's' : ''}
                  </span>
                  <span className="font-semibold flex-shrink-0">₹{baseTotal?.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Meal Plan</span>
                  <span className={`font-semibold ${selectedMealPlan === 'EP' ? 'text-green-600' : 'text-slate-800'}`}>
                    {selectedMealPlan === 'EP' ? 'FREE' :
                      selectedMealPlan === 'CP' ? 'Breakfast Incl.' :
                      selectedMealPlan === 'MAP' ? 'Breakfast & Dinner' : 'All Meals'}
                  </span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Coupon Discount</span>
                    <span className="font-semibold">-₹{discountAmount.toLocaleString('en-IN')}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">GST ({hotelPricing?.gst_price || 0}%)</span>
                  <span className="font-semibold">₹{gstAmount?.toLocaleString('en-IN') || '0'}</span>
                </div>
              </div>

              {/* Total */}
              <div className="flex justify-between items-center pt-4 mt-4 border-t border-gray-100">
                <span className="font-bold text-slate-900">Total</span>
                <span className="text-2xl font-black text-emerald-700">
                  ₹{totalBookingAmount?.toLocaleString('en-IN') || '0'}
                </span>
              </div>

              {/* Reserve */}
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleBookNow}
                startIcon={<Lock size={16} />}
                sx={{
                  mt: 3,
                  py: 1.75,
                  borderRadius: '12px',
                  fontWeight: 700,
                  fontSize: '1rem',
                  textTransform: 'none',
                  background: 'linear-gradient(to right, #059669, #047857)',
                  boxShadow: '0 10px 15px -3px rgba(16,185,129,0.3)',
                  transition: 'all .2s',
                  '&:hover': { background: 'linear-gradient(to right, #047857, #064e3b)', transform: 'translateY(-2px)' },
                }}
              >
                {bookingLoading ? 'Booking...' :
                 availabilityLoading ? 'Checking...' :
                 availability?.available ? 'Confirm & Reserve' :
                 'Select Room & Dates'}
              </Button>
              <p className="text-xs text-gray-500 text-center mt-3">You won't be charged yet</p>
            </div>
          </div>
        </div>
      </div>

      {/* Coupon modal */}
      {couponModalOpen && (
        <div className="fixed inset-0 z-[9999] bg-black/50 flex items-end md:items-center justify-center">
          <div className="bg-white w-full md:max-w-md rounded-t-2xl md:rounded-2xl max-h-[80vh] overflow-y-auto shadow-2xl">
            <div className="p-5 border-b border-gray-200 sticky top-0 bg-white flex justify-between items-center">
              <h2 className="text-lg font-bold">Available Coupons</h2>
              <button onClick={() => setCouponModalOpen(false)} className="p-1.5 rounded-full hover:bg-gray-100">
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              {couponLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto mb-2"></div>
                  <p className="text-sm text-gray-500">Loading coupons...</p>
                </div>
              ) : availableCoupons.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p>No coupons available</p>
                  <p className="text-sm mt-1">Check back later for discounts!</p>
                </div>
              ) : (
                availableCoupons.map((coupon) => (
                  <div key={coupon._id} className="border rounded-xl p-4 flex justify-between items-center gap-3 hover:shadow-md transition-shadow">
                    <div>
                      <p className="font-bold text-emerald-700 text-lg">{coupon.coupon_code}</p>
                      <p className="font-semibold text-green-600">
                        Save ₹{coupon.coupon_amount?.toLocaleString('en-IN') || 0}
                      </p>
                      {coupon.min_order_value > 0 && (
                        <p className="text-xs text-gray-400">Min order: ₹{coupon.min_order_value.toLocaleString('en-IN')}</p>
                      )}
                      {coupon.valid_until && (
                        <p className="text-xs text-gray-400">
                          Expires: {new Date(coupon.valid_until).toLocaleDateString('en-IN')}
                        </p>
                      )}
                    </div>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleSelectCoupon(coupon)}
                      disabled={couponLoading}
                      sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#059669', '&:hover': { bgcolor: '#047857' } }}
                    >
                      Apply
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {openBookingModal && (
        <BookingModal
          property={property}
          selectedAccommodation={selectedAccommodation}
          checkIn={checkIn ? checkIn.toISOString().split('T')[0] : ''}
          checkOut={checkOut ? checkOut.toISOString().split('T')[0] : ''}
          guests={selectedQuantity * 2}
          accommodationQuantity={selectedQuantity}
          coupon={couponData}
          discount={discountAmount}
          mealPlan={selectedMealPlan}
          mealPlanPrice={getEPOrMealPlanPrice(selectedAccommodation, selectedMealPlan)}
          eventId={checkoutEventId}
          onClose={() => {
            setOpenBookingModal(false);

            if (selectedAccommodation?._id) {
              setAvailability(null);
              setAvailabilityLoading(true);
              checkRoomAvailability(selectedAccommodation._id);
            }
          }}
        />
      )}

      {/* Terms modal */}
      {termsModalOpen && (
        <div className="fixed inset-0 z-[9999] bg-black/70 flex items-end sm:items-center justify-center sm:p-6">
          <div className="bg-white w-full sm:max-w-4xl max-h-[92dvh] sm:max-h-[90vh] flex flex-col shadow-2xl rounded-t-3xl sm:rounded-3xl overflow-hidden">
            <div className="p-4 sm:p-6 border-b border-gray-200 shrink-0">
              <div className="flex justify-between items-center gap-3">
                <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Terms & Conditions</h2>
                <button onClick={() => setTermsModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-xl">
                  <X className="w-6 h-6 text-gray-500" />
                </button>
              </div>
              <p className="text-xs sm:text-sm text-gray-500 mt-1">Required before booking</p>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6">
              {serviceTerms ? (
                <div
                  className="prose prose-sm sm:prose-lg max-w-none break-words [&_img]:max-w-full [&_img]:h-auto [&_table]:block [&_table]:overflow-x-auto"
                  dangerouslySetInnerHTML={{ __html: serviceTerms }}
                />
              ) : (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
                  <p className="text-gray-500">Loading terms...</p>
                </div>
              )}
            </div>

            <div className="shrink-0 p-4 sm:p-6 border-t bg-gradient-to-r from-emerald-50 to-teal-50 pb-[max(1rem,env(safe-area-inset-bottom))]">
              <label className="flex items-start gap-3 p-3 sm:p-4 bg-white rounded-xl border-2 border-dashed border-emerald-300 hover:border-emerald-500 cursor-pointer mb-4">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  className="mt-1 h-5 w-5 text-emerald-600 border-2 border-gray-300 rounded focus:ring-emerald-500 flex-shrink-0"
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                />
                <span className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                  I have read and agree to the <strong className="text-emerald-700 font-bold">Terms & Conditions</strong> above
                </span>
              </label>

              <div className="flex flex-col-reverse sm:flex-row gap-3">
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => setTermsModalOpen(false)}
                  sx={{ textTransform: 'none', py: 1.25, color: '#047857', borderColor: '#059669' }}
                >
                  Close
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  disabled={!termsAccepted}
                  onClick={() => {
                    setTermsModalOpen(false);
                    const eventId = crypto.randomUUID();
                    setCheckoutEventId(eventId);
                    trackEvent('InitiateCheckout', {
                      content_ids: [property._id],
                      content_type: 'hotel_room',
                      value: totalBookingAmount,
                      currency: 'INR',
                      num_items: selectedQuantity,
                    }, eventId);
                    setOpenBookingModal(true);
                  }}
                  sx={{
                    background: 'linear-gradient(to right, #059669, #047857)',
                    '&:hover': { background: 'linear-gradient(to right, #047857, #064e3b)' },
                    '&:disabled': { backgroundColor: '#fee2e2', color: '#fca5a5' },
                    textTransform: 'none', fontWeight: 'bold', py: 1.25,
                    boxShadow: '0 4px 12px rgba(5, 150, 105, 0.4)',
                  }}
                >
                  Accept & Continue
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile sticky bar */}
      {selectedAccommodation && showStickyBar && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 px-3 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-[0_-10px_20px_rgba(0,0,0,0.05)] md:hidden">
          <div className="flex items-center justify-between gap-3 w-full min-w-0">
            <div className="flex-shrink-0 min-w-0">
              <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wide">Total</p>
              <p className="text-base font-bold text-emerald-900 truncate">
                ₹{totalBookingAmount?.toLocaleString('en-IN') || '0'}
              </p>
            </div>

            <button
              onClick={() => {
                if (availability?.available) handleBookNow();
                else document.getElementById('accommodations')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white font-bold px-6 py-3 rounded-xl shadow-lg shadow-emerald-500/30 text-sm min-w-[140px]"
            >
              {availability?.available ? 'Book Now' : 'Select Dates'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}